import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardFooter, CardDescription } from './ui/card';
import { Label } from './ui/label';
import { Settings as SettingsIcon, Save, Server, Mail, Shield, Activity, Lock, Plus, Trash2, Edit2, Check, X, Users } from 'lucide-react';
import { toast } from 'sonner';
import { getMailAccounts, addMailAccount, updateMailAccount, deleteMailAccount } from '../api/api';
import { useConfirm } from '../contexts/ConfirmContext';
import { Separator } from './ui/separator';
import TeamSettings from './TeamSettings';
import PlanBilling from './PlanBilling';
import { CreditCard } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';

const initialAccountState = {
  user: '',
  pass: '',
  from: '',
  dailyLimit: 10,
  minIntervalSeconds: 300,
  active: true,
  smtpHost: 'smtp.gmail.com',
  smtpPort: 465,
  secure: true,
  imapHost: 'imap.gmail.com',
  imapPort: 993,
  imapUser: '',
  imapPass: '',
  imapSecure: true
};

export default function Settings() {
  // General Settings
  const [signature, setSignature] = useState(localStorage.getItem('signature') || 'Best regards,\nÖmer Faruk Yılmaz');
  const [subject, setSubject] = useState(localStorage.getItem('subject') || 'Job Application: Software Developer');

  // Mail Account Settings
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentAccount, setCurrentAccount] = useState(initialAccountState);
  const [isEditing, setIsEditing] = useState(false);
  const [tab, setTab] = useState('mail');
  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const res = await getMailAccounts();
      if (res.data.accounts) {
        setAccounts(res.data.accounts);
      }
    } catch (error) {
      console.error("Failed to fetch mail accounts", error);
      toast.error("Failed to load mail accounts.");
    } finally {
      setLoading(false);
    }
  };

  const handleGeneralSave = () => {
    localStorage.setItem('signature', signature);
    localStorage.setItem('subject', subject);
    toast.success('General settings saved!');
  };

  const handleOpenDialog = (account = null) => {
    if (account) {
      setCurrentAccount(account);
      setIsEditing(true);
    } else {
      setCurrentAccount(initialAccountState);
      setIsEditing(false);
    }
    setIsDialogOpen(true);
  };

  const handleAccountSave = async () => {
    try {
      if (isEditing && currentAccount._id) {
        const res = await updateMailAccount(currentAccount._id, currentAccount);
        setAccounts(prev => prev.map(acc => acc._id === currentAccount._id ? res.data.account : acc));
        toast.success('Account updated successfully!');
      } else {
        const res = await addMailAccount(currentAccount);
        setAccounts(prev => [...prev, res.data.account]);
        toast.success('Account created successfully!');
      }
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Failed to save mail account", error);
      toast.error('Failed to save account.');
    }
  };

  const confirm = useConfirm();

  const handleDelete = async (id) => {
    const ok = await confirm("Delete Mail Account?", "Are you sure you want to delete this account? This action cannot be undone.");
    if (!ok) return;
    try {
      await deleteMailAccount(id);
      setAccounts(prev => prev.filter(acc => acc._id !== id));
      toast.success('Account deleted.');
    } catch (error) {
      console.error("Failed to delete account", error);
      toast.error('Failed to delete account.');
    }
  };

  const handleChange = (field, value) => {
    setCurrentAccount(prev => ({ ...prev, [field]: value }));
  };

  const toggleActive = async (account) => {
    try {
      const updated = { ...account, active: !account.active };
      // If we update active status, we might need to deactivate others if business logic requires only one active.
      // But backend allows multiple active? Let's assumes yes for now, or backend handles logic.
      // The user logic seemed to pick "findOne({active: true})" which gets the first one.
      // To be safe, if we activate one, maybe we should warn? Or just let it be.

      await updateMailAccount(account._id, updated);
      setAccounts(prev => prev.map(acc => acc._id === account._id ? updated : acc));
      toast.success(`Account ${updated.active ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error("Failed to update status");
    }
  }

  if (loading) {
    return <div className="p-10 text-center">Loading settings...</div>;
  }

  return (
    <div className="flex flex-col items-center p-6 lg:p-10 gap-8 animate-in fade-in duration-500">

      {/* Tab Navigation */}
      <div className="w-full max-w-4xl flex gap-1 border border-border rounded-xl p-1 bg-muted/20">
        {[{ id: 'mail', label: 'Mail Accounts', icon: Mail }, { id: 'general', label: 'General', icon: SettingsIcon }, { id: 'team', label: 'Team', icon: Users }, { id: 'billing', label: 'Subscription', icon: CreditCard }].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-1.5 px-3 rounded-lg text-sm font-medium transition-all ${
              tab === t.id ? 'bg-background text-foreground shadow-sm border border-border' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <t.icon className="h-4 w-4" />{t.label}
          </button>
        ))}
      </div>

      {/* Team Tab */}
      {tab === 'team' && (
        <div className="w-full max-w-4xl">
          <TeamSettings />
        </div>
      )}

      {/* Subscription Tab */}
      {tab === 'billing' && (
        <div className="w-full max-w-4xl">
          <PlanBilling />
        </div>
      )}

      {/* Mail & General Tabs */}
      {tab !== 'team' && (<>
      {/* Mail Configuration List Card */}
      <Card className="w-full max-w-4xl shadow-lg border-t-4 border-t-blue-600">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
              <Server className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <CardTitle className="text-2xl font-bold">Mail Accounts</CardTitle>
              <CardDescription>Manage your connected email accounts.</CardDescription>
            </div>
          </div>
          <Button onClick={() => handleOpenDialog()} className="gap-2">
            <Plus className="h-4 w-4" /> Add Account
          </Button>
        </CardHeader>

        <CardContent className="space-y-4 pt-6">
          {accounts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
              <Mail className="h-10 w-10 mx-auto mb-3 opacity-20" />
              <p>No mail accounts configured.</p>
              <Button variant="link" onClick={() => handleOpenDialog()}>Add your first account</Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {accounts.map(account => (
                <div key={account._id} className="flex items-center justify-between p-4 border rounded-lg bg-card hover:bg-muted/10 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-full ${account.active ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                      <Mail className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold">{account.user}</h4>
                        {account.active ? (
                          <Badge variant="success" className="bg-green-100 text-green-700 hover:bg-green-100 border-0 h-5 text-[10px]">Active</Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground h-5 text-[10px]">Inactive</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{account.smtpHost}:{account.smtpPort}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={() => toggleActive(account)} title={account.active ? "Deactivate" : "Activate"}>
                      {account.active ? <Check className="h-4 w-4 text-green-600" /> : <X className="h-4 w-4 text-muted-foreground" />}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(account)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive/90" onClick={() => handleDelete(account._id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* General Settings Card */}
      <Card className="w-full max-w-4xl shadow-lg">
        <CardHeader className="flex flex-row items-center gap-4 pb-2">
          <div className="p-3 rounded-full bg-orange-100 dark:bg-orange-900/30">
            <SettingsIcon className="h-8 w-8 text-orange-600 dark:text-orange-400" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold">General Templates</CardTitle>
            <CardDescription>Default subject and signature for new emails.</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-6 pt-6">
          <div className="space-y-2">
            <Label htmlFor="subject">Default Email Subject</Label>
            <Input
              id="subject"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Job Application..."
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="signature">Email Signature</Label>
            <Textarea
              id="signature"
              value={signature}
              onChange={e => setSignature(e.target.value)}
              rows={5}
              placeholder="Your signature..."
            />
          </div>
        </CardContent>
        <CardFooter className="justify-end bg-muted/20 py-4 rounded-b-lg">
          <Button onClick={handleGeneralSave} className="font-semibold w-full md:w-auto" variant="outline">
            <Save className="mr-2 h-4 w-4" />
            Save Templates
          </Button>
        </CardFooter>
      </Card>

      {/* Account Dialog */}
      <Sheet open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <SheetContent side="right" className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{isEditing ? 'Edit Mail Account' : 'Add New Mail Account'}</SheetTitle>
            <SheetDescription>
              Configure SMTP and IMAP settings.
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-6 py-4">
            {/* Credentials */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Credentials</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={currentAccount.user} onChange={e => handleChange('user', e.target.value)} placeholder="email@example.com" />
                </div>
                <div className="space-y-2">
                  <Label>App Password</Label>
                  <Input type="password" value={currentAccount.pass} onChange={e => handleChange('pass', e.target.value)} placeholder="••••••••" />
                </div>
                <div className="space-y-2">
                  <Label>From Name</Label>
                  <Input value={currentAccount.from} onChange={e => handleChange('from', e.target.value)} placeholder="Name <email@example.com>" />
                </div>
                <div className="space-y-2 flex items-center gap-2 pt-8">
                  <div className="flex items-center space-x-2">
                    <Switch id="active-mode" checked={currentAccount.active} onCheckedChange={(checked) => handleChange('active', checked)} />
                    <Label htmlFor="active-mode">Active</Label>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* SMTP */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2"><Activity className="h-4 w-4" /> SMTP (Sending)</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label>Host</Label>
                  <Input value={currentAccount.smtpHost} onChange={e => handleChange('smtpHost', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Port</Label>
                  <Input type="number" value={currentAccount.smtpPort} onChange={e => handleChange('smtpPort', parseInt(e.target.value))} />
                </div>
                <div className="space-y-2 flex items-center gap-2">
                  <div className="flex items-center space-x-2">
                    <Switch id="smtp-secure" checked={currentAccount.secure} onCheckedChange={(checked) => handleChange('secure', checked)} />
                    <Label htmlFor="smtp-secure">Secure</Label>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* IMAP */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2"><Shield className="h-4 w-4" /> IMAP (Receiving)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>User</Label>
                  <Input value={currentAccount.imapUser} onChange={e => handleChange('imapUser', e.target.value)} placeholder="defaults to email" />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input type="password" value={currentAccount.imapPass} onChange={e => handleChange('imapPass', e.target.value)} placeholder="defaults to pass" />
                </div>
                <div className="space-y-2">
                  <Label>Host</Label>
                  <Input value={currentAccount.imapHost} onChange={e => handleChange('imapHost', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Port</Label>
                  <Input type="number" value={currentAccount.imapPort} onChange={e => handleChange('imapPort', parseInt(e.target.value))} />
                </div>
                <div className="space-y-2 flex items-center gap-2">
                  <div className="flex items-center space-x-2">
                    <Switch id="imap-secure" checked={currentAccount.imapSecure} onCheckedChange={(checked) => handleChange('imapSecure', checked)} />
                    <Label htmlFor="imap-secure">Secure</Label>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Limits */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2"><Lock className="h-4 w-4" /> Limits</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Daily Limit</Label>
                  <Input type="number" value={currentAccount.dailyLimit} onChange={e => handleChange('dailyLimit', parseInt(e.target.value))} />
                </div>
                <div className="space-y-2">
                  <Label>Min Interval (s)</Label>
                  <Input type="number" value={currentAccount.minIntervalSeconds} onChange={e => handleChange('minIntervalSeconds', parseInt(e.target.value))} />
                </div>
              </div>
            </div>
          </div>

          <SheetFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAccountSave}>Save Account</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      </>)}

    </div>
  );
}